import type { CategoryFilterState } from './catalogCategoryTypes';
import type { CategoryRecord } from './categoryTypes';
import { matchesSearchText } from '../../shared/format/searchText';

export type CategoryTreeGroup = {
  parent: CategoryRecord;
  children: CategoryRecord[];
};

export type VisibleCategoryGroup = {
  group: CategoryTreeGroup;
  visibleChildren: CategoryRecord[];
};

const sortCategories = (rows: CategoryRecord[]): CategoryRecord[] =>
  [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

export function buildCategoryTreeGroups(categories: CategoryRecord[]): CategoryTreeGroup[] {
  const byParent = new Map<string | null, CategoryRecord[]>();
  for (const category of categories) {
    const key = category.parentId ?? null;
    const list = byParent.get(key) ?? [];
    list.push(category);
    byParent.set(key, list);
  }

  const roots = sortCategories(byParent.get(null) ?? []);
  const groups: CategoryTreeGroup[] = roots.map((parent) => ({
    parent,
    children: sortCategories(byParent.get(parent.id) ?? []),
  }));

  const groupedIds = new Set<string>();
  for (const { parent, children } of groups) {
    groupedIds.add(parent.id);
    for (const child of children) groupedIds.add(child.id);
  }

  for (const category of categories) {
    if (groupedIds.has(category.id)) continue;
    groups.push({ parent: category, children: [] });
  }

  return groups;
}

function matchesSearch(group: CategoryTreeGroup, child: CategoryRecord, query: string): boolean {
  return matchesSearchText(group.parent.name, query) || matchesSearchText(child.name, query);
}

function visibleChildrenForGroup(
  group: CategoryTreeGroup,
  filterState: CategoryFilterState,
  query: string,
): CategoryRecord[] {
  return group.children.filter((child) => {
    if (!matchesSearch(group, child, query)) return false;
    if (filterState === 'active') return group.parent.isActive && child.isActive;
    if (filterState === 'inactive') return !child.isActive;
    return true;
  });
}

export function filterVisibleCategoryGroups(
  categories: CategoryRecord[],
  filterState: CategoryFilterState,
  searchQuery: string,
): VisibleCategoryGroup[] {
  const query = searchQuery.trim();
  const groups = buildCategoryTreeGroups(categories);

  return groups
    .map((group) => {
      const visibleChildren = visibleChildrenForGroup(group, filterState, query);
      const groupMatch =
        !query || matchesSearchText(group.parent.name, query) || visibleChildren.length > 0;
      if (!groupMatch) return null;

      const showGroup =
        filterState === 'all' ||
        (filterState === 'active' &&
          group.parent.isActive &&
          (group.children.length === 0 || group.children.some((child) => child.isActive))) ||
        (filterState === 'inactive' &&
          (!group.parent.isActive || group.children.some((child) => !child.isActive)));

      if (!showGroup) return null;

      return { group, visibleChildren };
    })
    .filter((row): row is VisibleCategoryGroup => row != null);
}

export function countActiveChildren(group: CategoryTreeGroup): number {
  return group.children.filter((child) => child.isActive).length;
}

/**
 * Mueve una categoría un puesto dentro de su grupo de hermanos
 * (raíces entre raíces, hijas entre hijas del mismo padre).
 * Devuelve el sortOrder nuevo de todo el grupo, o null si no puede moverse.
 */
export function reorderSiblingGroup(
  categories: CategoryRecord[],
  categoryId: string,
  direction: -1 | 1,
): { id: string; sortOrder: number }[] | null {
  const target = categories.find((row) => row.id === categoryId);
  if (!target) return null;

  const siblings = categories
    .filter((row) => (row.parentId ?? null) === (target.parentId ?? null))
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
    );

  const index = siblings.findIndex((row) => row.id === categoryId);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= siblings.length) return null;

  const reordered = [...siblings];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(next, 0, moved);

  return reordered.map((row, position) => ({ id: row.id, sortOrder: (position + 1) * 10 }));
}
